import React, { useEffect, useRef, useState } from 'react';
import { useI18n } from '../contexts/I18nContext';

interface InlineCodeEditorProps {
  isVisible: boolean;
  position: { x: number; y: number; width: number; height: number } | null;
  code: string;
  language: string;
  fontSize?: number;
  backgroundColor?: string;
  zIndex?: number;
  onCodeChange: (code: string) => void;
  onLanguageChange: (language: string) => void;
  onComplete: () => void;
  onCancel: () => void;
}

const InlineCodeEditor: React.FC<InlineCodeEditorProps> = ({
  isVisible,
  position,
  code,
  language,
  fontSize = 11,
  backgroundColor = '#FFFFFF',
  zIndex = 1001,
  onCodeChange,
  onLanguageChange,
  onComplete,
  onCancel
}) => {
  const { t } = useI18n();
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // 배경색에 따라 텍스트 색상 결정
  const getTextColor = (bgColor: string) => {
    // hex 색상을 RGB로 변환
    const hex = bgColor.replace('#', '');
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    
    // 밝기 계산 (0-255)
    const brightness = (r * 299 + g * 587 + b * 114) / 1000;
    
    // 밝기가 128 이상이면 어두운 텍스트, 아니면 밝은 텍스트
    return brightness > 128 ? '#1a1a1a' : '#d4d4d4';
  };

  const textColor = getTextColor(backgroundColor);

  useEffect(() => {
    if (isVisible && textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [isVisible]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && e.ctrlKey) {
      // Ctrl+Enter: 입력 완료
      e.preventDefault();
      onComplete();
    } else if (e.key === 'Escape') {
      // Escape: 입력 취소
      e.preventDefault();
      onCancel();
    }
  };

  const languages = [
    { value: 'javascript', label: 'JavaScript' },
    { value: 'typescript', label: 'TypeScript' },
    { value: 'python', label: 'Python' },
    { value: 'java', label: 'Java' },
    { value: 'cpp', label: 'C++' },
    { value: 'csharp', label: 'C#' },
    { value: 'go', label: 'Go' },
    { value: 'rust', label: 'Rust' },
    { value: 'html', label: 'HTML' },
    { value: 'css', label: 'CSS' },
    { value: 'json', label: 'JSON' },
    { value: 'sql', label: 'SQL' },
  ];

  if (!isVisible || !position) return null;

  return (
    <div
      style={{
        position: 'absolute',
        left: position.x,
        top: position.y,
        width: position.width,
        height: position.height,
        zIndex: zIndex,
        border: 'none',
        borderRadius: '8px',
        backgroundColor: backgroundColor,
        overflow: 'hidden',
        boxShadow: 'none',
      }}
    >
      {/* 툴바 */}
      <div style={{ 
        padding: '4px 8px', 
        borderBottom: textColor === '#1a1a1a' ? '1px solid #e5e7eb' : '1px solid #404040', 
        display: 'flex', 
        gap: '8px',
        alignItems: 'center',
        background: backgroundColor
      }}>
        <select
          value={language}
          onChange={(e) => onLanguageChange(e.target.value)}
          style={{
            padding: '2px 8px',
            fontSize: '12px',
            border: '1px solid #e5e7eb',
            borderRadius: '3px',
            background: 'white',
            color: '#374151',
            cursor: 'pointer'
          }}
        >
          {languages.map(lang => (
            <option key={lang.value} value={lang.value}>{lang.label}</option>
          ))}
        </select>
        <div style={{ flex: 1 }}></div>
        <span style={{ fontSize: '10px', color: textColor === '#1a1a1a' ? '#6b7280' : '#9ca3af' }}>{t.modals.codeEditor.hint}</span>
      </div>

      {/* 편집 영역 */}
      <textarea
        ref={textareaRef}
        style={{
          width: '100%',
          height: 'calc(100% - 33px)',
          padding: '6px',
          border: 'none',
          outline: 'none',
          fontSize: `${fontSize}px`,
          fontFamily: "'Courier New', monospace",
          lineHeight: 1.4,
          resize: 'none',
          boxSizing: 'border-box',
          backgroundColor: backgroundColor,
          color: textColor,
          whiteSpace: 'pre',
          overflow: 'auto',
        }}
        value={code}
        onChange={(e) => onCodeChange(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder={t.modals.codeEditor.placeholder}
        spellCheck={false}
      />
    </div>
  );
};

export default InlineCodeEditor;

